import {
  AssigneeDirectory,
  type AssigneeDirectoryEntry,
  AuditEventListResponse,
  type AuditEventView,
  Comment,
  DetachedParentDirectory,
  type DetachedParentEntry,
  FieldDefinition,
  Project,
  ProjectDirectory,
  type ProjectDirectoryEntry,
  ProjectMember,
  State,
  Task,
  TaskAssignee,
  type TaskBulkOp,
  type TaskCreateRequest,
  TaskCreateRequestSchema,
  TaskDependency,
  TaskListItem,
  type TaskMoveInput,
  TaskMovePreview,
  TaskProjectDirectoryEntry,
  TaskProjectMembership,
  TaskRef,
} from '@staiter/shared/task-module';
import { z } from 'zod';
import { apiRequest } from '~/lib/api/client';
import { captureShapeMismatch } from '~/lib/sentry/capture';
import type { GroupBy, TaskFilter } from '../queryKeys';
import { migrateStateGroups } from '../stateGroupsMigration.js';
import { LabelDirectory, withDirectoryLabels } from './label-directory';

/**
 * Task-module API client (FE-S12.1-11 + FE-S12.2 alignment).
 *
 * Aligned с BE response shapes из `apps/backend/src/api/task-module-*.ts`:
 * BE wraps payloads (`{tasks: [...], projects: [...]}` etc.) — FE распаковывает
 * через `.transform`. Это даёт single-source-of-truth contract без drift.
 *
 * Path prefix: /api/v1/{tasks,projects,...} обрабатывается клиентом apiRequest.
 */

// ─── Lists / Reads ──────────────────────────────────────────────────

const TasksEnvelope = z
  .object({
    // FU-FND-1 — per-row resilience: список парсим построчно, а не строгим
    // z.array(Task). Одна битая задача (напр. папка с проставленной датой —
    // нарушение refinement) раньше роняла строгий парс ВСЕГО массива → рендер
    // всего модуля падал с toast «Несовместимый ответ сервера». Теперь валидные
    // рендерим, битые дропаем + сигналим в Sentry (capped blast radius).
    tasks: z.array(z.unknown()),
    next_cursor: z.string().nullable(),
    // BE-1 — denorm-каталог ответственных по всему списку (опц. для обратной совместимости).
    assignee_directory: AssigneeDirectory.optional().default([]),
    // BE-3 — denorm-каталог проектов (code/name) для project-chip с drill (опц.).
    project_directory: ProjectDirectory.optional().default([]),
    // BA1 — денорм статусов задач НЕЗАВИСИМО от проекта запроса: вторичная доска
    // рендерит реальный чип статуса главного и бакетит по group (FA1).
    state_directory: z
      .array(
        z.object({
          state_id: z.string(),
          name: z.string(),
          color_token: z.string(),
          group: z.string(),
          // BE-19 — проект статуса: без него кросс-проектная доска не понимает, в чей
          // workflow ведёт статус, и рисует заголовок из обрубка.
          project_id: z.string().nullable().optional(),
        }),
      )
      .optional()
      .default([]),
    // F14 — имена этапов, вырезанных фильтром выдачи. Без них дети скрытой папки
    // рисуются корнями, и отмена этапа выглядит как рассыпавшийся план.
    detached_parent_directory: DetachedParentDirectory.optional().default([]),
    // tasks-by-company B5 — denorm имён компаний (чип «Компания» в holding/all-агрегате).
    entities: z
      .record(z.string(), z.object({ name: z.string() }))
      .optional()
      .default({}),
    // B4 (пак context-menu) — на какие задачи выдачи подписан текущий пользователь.
    // Sibling-массивом, как остальные денормы: strict Task не раздуваем.
    watched_task_ids: z.array(z.string()).optional().default([]),
    // F97 (WTP-B3) — справочник меток выдачи: метки строк собираются из `label_ids`.
    label_directory: LabelDirectory,
    // BE-12/BE-13 — полнота выдачи. Без них счётчики групп («Проект A · 12») выдают
    // длину обрезанного массива за полное число задач и врут уверенно.
    total_count: z.number().int().nonnegative().optional(),
    truncated: z.boolean().optional(),
  })
  .transform((d) => {
    // F12 — строки списка разбираются ЛЁГКОЙ схемой `TaskListItem`: без четырёх
    // `.refine()` полной задачи (они выполняются на каждой из сотен строк и
    // проверяют то, что сервер гарантирует при записи) и без обязательных
    // описаний (B8/B9 убирают их из выдачи). Схема одна на list, gantt и search
    // `[Б16]`; построчный `safeParse` сохранён — одна битая строка не роняет
    // весь список (FU-FND-1) `[П15]`.
    const tasks: TaskListItem[] = [];
    let dropped = 0;
    for (const raw of d.tasks) {
      const r = TaskListItem.safeParse(raw);
      if (r.success) tasks.push(withDirectoryLabels(r.data, d.label_directory));
      else {
        dropped += 1;
        captureShapeMismatch('task-list-row', r.error, raw);
      }
    }
    if (dropped > 0 && typeof console !== 'undefined') {
      console.warn(`[tasks] отброшено ${dropped} невалидных задач из списка (schema drift)`);
    }
    return {
      tasks,
      nextCursor: d.next_cursor,
      assigneeDirectory: d.assignee_directory,
      projectDirectory: d.project_directory,
      stateDirectory: d.state_directory,
      detachedParentDirectory: d.detached_parent_directory,
      entities: d.entities,
      watchedTaskIds: d.watched_task_ids,
      totalCount: d.total_count ?? tasks.length,
      truncated: d.truncated ?? false,
    };
  });
export type TaskList = {
  tasks: TaskListItem[];
  nextCursor: string | null;
  assigneeDirectory: AssigneeDirectoryEntry[];
  projectDirectory: ProjectDirectoryEntry[];
  stateDirectory: Array<{
    state_id: string;
    name: string;
    color_token: string;
    group: string;
    project_id?: string | null;
  }>;
  /** F14 — имена вырезанных фильтром родителей (крошка над их детьми). */
  detachedParentDirectory: DetachedParentEntry[];
  entities: Record<string, { name: string }>;
  /** B4 — задачи выдачи, на которые подписан текущий пользователь. */
  watchedTaskIds: string[];
  /** Полное число задач под фильтром — не длина (возможно обрезанного) массива. */
  totalCount: number;
  /** Выдача упёрлась в кап ганта: состав дерева, дорожек и счётчиков неполон. */
  truncated: boolean;
};

export async function fetchTasks(params: {
  /** F48 (WTP-B3) — поля строки по настройке колонок. */
  fields?: string;
  projectId?: string;
  /** BE-12 — гант дашборда: несколько проектов сразу (кросс-проектная выдача). */
  projectIds?: readonly string[];
  stateId?: string;
  subjectType?: string;
  subjectId?: string;
  filter?: TaskFilter;
  groupBy?: GroupBy;
  cursor?: string;
  limit?: number;
  includeArchived?: boolean;
  /** work-tasks-defaults B1/F1 — фильтр по группе статуса (скрытие закрытых по умолчанию). */
  stateGroups?: readonly string[];
  /** S20 B-10 / BE-12 — gantt-режим: полная выдача (кап на BE), в т.ч. без проекта. */
  view?: 'gantt';
  /** F14/B10 — не считать COUNT списка: числа берутся из ручки счётчиков. */
  includeTotal?: boolean;
  /** F14/B11 — серверная сортировка страницы (whitelist на бэкенде). */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}): Promise<TaskList> {
  const search = new URLSearchParams();
  if (params.projectId) search.set('project_id', params.projectId);
  if (params.projectIds && params.projectIds.length > 0) {
    search.set('project_ids', params.projectIds.join(','));
  }
  if (params.stateId) search.set('state_id', params.stateId);
  if (params.subjectType) search.set('subject_type', params.subjectType);
  if (params.subjectId) search.set('subject_id', params.subjectId);
  if (params.cursor) search.set('cursor', params.cursor);
  if (params.limit) search.set('limit', String(params.limit));
  if (params.includeArchived) search.set('include_archived', 'true');
  if (params.stateGroups && params.stateGroups.length > 0) {
    // F10 [П18] — та же миграция на чтении, что у поиска: сохранённый вид или
    // ссылка из переписки не знает про `paused`, и без этого задача на паузе
    // исчезала бы из вида навсегда.
    search.set('state_groups', migrateStateGroups(params.stateGroups).join(','));
  }
  if (params.view) search.set('view', params.view);
  if (params.includeTotal === false) search.set('include_total', 'false');
  if (params.fields) search.set('fields', params.fields);
  if (params.sortBy) {
    search.set('sort_by', params.sortBy);
    if (params.sortDir) search.set('sort_dir', params.sortDir);
  }
  const qs = search.toString();
  return apiRequest(`/task-module/tasks${qs ? `?${qs}` : ''}`, TasksEnvelope);
}

/** BE-1 — задача + denorm-каталог ответственных (display_name/avatar). */
export type TaskWithDirectory = {
  task: Task;
  assigneeDirectory: AssigneeDirectoryEntry[];
  /** FA2 (M2M) — денорм статуса главного проекта (BA1): карточка из вторичного
   *  контекста рендерит реальный чип и знает, что статус read-only. */
  stateDirectory: Array<{ state_id: string; name: string; color_token: string; group: string }>;
  /** FA2/FB8 — денорм всех member-проектов {project_id, code, name}. */
  projectDirectory: ProjectDirectoryEntry[];
  /**
   * F1 (project-task-progress §1.3) — роллап прогресса по подзадачам ГЛАВНОГО
   * проекта. `null` = подзадач нет, задача сама лист → карточка даёт ползунок.
   * Иерархия per-project, поэтому источник дерева подписан в UI явно.
   */
  progressRollup: string | null;
  progressRollupLeafCount: number;
};

const TaskDetailEnvelope = z
  .object({
    task: Task,
    assignees: z.array(z.unknown()).optional().default([]),
    watchers: z.array(z.unknown()).optional().default([]),
    // BE-1 — UUID-keyed bundle name+avatar (strict; не мутирует Task).
    assignee_directory: AssigneeDirectory.optional().default([]),
    state_directory: z
      .array(
        z.object({
          state_id: z.string(),
          name: z.string(),
          color_token: z.string(),
          group: z.string(),
        }),
      )
      .optional()
      .default([]),
    project_directory: ProjectDirectory.optional().default([]),
    label_directory: LabelDirectory,
    // F1 — роллап по primary-дереву. `.optional()` — forward-compat: бэкенд до
    // выката поля не шлёт, карточка деградирует к «нет подзадач».
    progress_rollup: z
      .string()
      .regex(/^-?\d+(\.\d+)?$/)
      .nullable()
      .optional()
      .default(null),
    progress_rollup_leaf_count: z.number().int().nonnegative().optional().default(0),
  })
  .transform((d) => ({
    task: withDirectoryLabels(d.task, d.label_directory),
    assigneeDirectory: d.assignee_directory,
    stateDirectory: d.state_directory,
    projectDirectory: d.project_directory,
    progressRollup: d.progress_rollup,
    progressRollupLeafCount: d.progress_rollup_leaf_count,
  }));

export async function fetchTask(taskId: string): Promise<TaskWithDirectory> {
  return apiRequest(`/task-module/tasks/${taskId}`, TaskDetailEnvelope);
}

const CommentsEnvelope = z.object({ comments: z.array(Comment) }).transform((d) => d.comments);

const TaskIncludeEnvelope = z
  .object({
    task: Task,
    comments: z.array(Comment).optional(),
    next_comments_cursor: z.string().nullable().optional(),
    dependencies: z.array(TaskDependency).optional(),
    field_definitions: z.array(FieldDefinition).optional(),
    // BE-1 — denorm-каталог ответственных (опц.).
    assignee_directory: AssigneeDirectory.optional().default([]),
    label_directory: LabelDirectory,
    // F1 (project-task-progress §1.3) — роллап по primary-дереву. Peek читает его
    // из того же bundle, что и карточка: иначе панель показывала бы ползунок там,
    // где страница показывает сводку по подзадачам.
    progress_rollup: z
      .string()
      .regex(/^-?\d+(\.\d+)?$/)
      .nullable()
      .optional()
      .default(null),
  })
  .transform(({ label_directory, ...d }) => ({
    ...d,
    task: withDirectoryLabels(d.task, label_directory),
  }));

/** S17.3 F-C2 ↔ B-1 — peek одним roundtrip (include=). */
export async function fetchTaskWithIncludes(taskId: string): Promise<{
  task: Task;
  comments?: Comment[];
  next_comments_cursor?: string | null;
  dependencies?: TaskDependency[];
  field_definitions?: FieldDefinition[];
  assignee_directory: AssigneeDirectoryEntry[];
  progress_rollup: string | null;
}> {
  return apiRequest(
    `/task-module/tasks/${taskId}?include=comments,dependencies,field_definitions`,
    TaskIncludeEnvelope,
  );
}

export async function fetchTaskComments(taskId: string): Promise<Comment[]> {
  return apiRequest(`/task-module/tasks/${taskId}/comments`, CommentsEnvelope);
}

const CommentsPageEnvelope = z.object({
  comments: z.array(Comment),
  next_cursor: z.string().nullable().optional(),
  // BE-11 — denorm-каталог авторов/упомянутых (опц.).
  comment_directory: AssigneeDirectory.optional().default([]),
});
export type CommentsPage = {
  comments: Comment[];
  next_cursor: string | null;
  commentDirectory: AssigneeDirectoryEntry[];
};

/** FE-S12.3-8 — страница комментариев для infinite scroll. */
export async function fetchTaskCommentsPage(
  taskId: string,
  cursor?: string,
): Promise<CommentsPage> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const r = await apiRequest(`/task-module/tasks/${taskId}/comments${qs}`, CommentsPageEnvelope);
  return {
    comments: r.comments,
    next_cursor: r.next_cursor ?? null,
    commentDirectory: r.comment_directory,
  };
}

const DepsEnvelope = z
  .object({ dependencies: z.array(TaskDependency) })
  .transform((d) => d.dependencies);

export async function fetchTaskDependencies(taskId: string): Promise<TaskDependency[]> {
  return apiRequest(`/task-module/tasks/${taskId}/dependencies`, DepsEnvelope);
}

// ─── Projects ───────────────────────────────────────────────────────

const ProjectsEnvelope = z.object({ projects: z.array(Project) }).transform((d) => d.projects);

export async function fetchProjects(): Promise<Project[]> {
  return apiRequest('/task-module/projects', ProjectsEnvelope);
}

/**
 * F1 (cross-company-move §2.1) — цели переноса: проекты ВСЕХ компаний членства,
 * независимо от активного scope. Отдельный запрос, а не параметр к обычному
 * листингу: у них разная семантика (права vs отображение) и разный кэш-ключ —
 * иначе воркспейс начал бы показывать чужие проекты.
 */
const MoveTargetsEnvelope = z.object({
  projects: z.array(Project),
  // Имена компаний приезжают тем же ответом — отдельный запрос не нужен.
  entities: z
    .record(z.object({ name: z.string() }))
    .optional()
    .default({}),
});

export type MoveTargets = z.infer<typeof MoveTargetsEnvelope>;

export async function fetchMoveTargetProjects(): Promise<MoveTargets> {
  return apiRequest('/task-module/projects?targets=move', MoveTargetsEnvelope);
}

const ProjectDetailEnvelope = z
  .object({
    project: Project,
    members: z.array(z.unknown()).optional().default([]),
  })
  .transform((d) => d.project);

export async function fetchProject(projectId: string): Promise<Project> {
  return apiRequest(`/task-module/projects/${projectId}`, ProjectDetailEnvelope);
}

// FE-9 — denorm-каталог участников проекта (BE-4): id+name+email+avatar.
// Источник `users`-prop для пикеров ответственного и списка в настройках.
const ProjectMembersEnvelope = z
  .object({ members: z.array(ProjectMember) })
  .transform((d) => d.members);

export async function fetchProjectMembers(projectId: string): Promise<ProjectMember[]> {
  return apiRequest(`/task-module/projects/${projectId}/members`, ProjectMembersEnvelope);
}

// BUG-3 — кандидаты в ответственные/упоминания = пользователи компании (entity),
// не только участники проекта. Пул для AssigneePopover + @mention.
const AssignableUser = z.object({
  id: z.string(),
  email: z.string().nullable(),
  full_name: z.string().nullable(),
});
const AssignableUsersEnvelope = z
  .object({ users: z.array(AssignableUser) })
  .transform((d) => d.users);

export async function fetchAssignableUsers(
  projectId: string,
): Promise<{ id: string; email: string | null; full_name: string | null }[]> {
  return apiRequest(`/task-module/projects/${projectId}/assignable-users`, AssignableUsersEnvelope);
}

// go-live 2026-07-03 — пул ответственных для глобальной страницы задач (без проекта):
// scope-aware (активная компания / холдинг / «Все компании»).
// project-visibility (F10) — с `projectIds` бэкенд сужает пул до участников этих
// проектов (§3.4): в контексте задачи закрытого проекта нельзя предлагать не-участников.
// project-visibility (F17/B24, §3.8) — `opts.q` серверный поиск; `opts.entityId` —
// пул конкретной компании (форма создания мульти-компанийная: пул следует за Select).
export async function fetchGlobalAssignableUsers(
  projectIds?: readonly string[],
  opts?: { q?: string; entityId?: string },
): Promise<{ id: string; email: string | null; full_name: string | null }[]> {
  const qs = new URLSearchParams();
  if (projectIds && projectIds.length > 0) qs.set('project_ids', projectIds.join(','));
  if (opts?.q) qs.set('q', opts.q);
  if (opts?.entityId) qs.set('entity_id', opts.entityId);
  const suffix = qs.toString();
  return apiRequest(
    `/task-module/tasks/assignable-users${suffix ? `?${suffix}` : ''}`,
    AssignableUsersEnvelope,
  );
}

const StatesEnvelope = z.object({ states: z.array(State) }).transform((d) => d.states);

export async function fetchProjectStates(projectId: string): Promise<State[]> {
  return apiRequest(`/task-module/projects/${projectId}/states`, StatesEnvelope);
}

/**
 * BE-19 — справочник статусов сразу по N проектам. Общий дашборд держит задачи из
 * всех проектов компании: per-project ручкой это N запросов на каждый рендер.
 *
 * Пустой список → пустая выдача без запроса (а НЕ «все статусы компании»: без
 * project_ids бэкенд отдаёт периметр компании, и это был бы другой ответ).
 */
export async function fetchStatesForProjects(projectIds: readonly string[]): Promise<State[]> {
  if (projectIds.length === 0) return [];
  const qs = new URLSearchParams({ project_ids: projectIds.join(',') });
  return apiRequest(`/task-module/states?${qs.toString()}`, StatesEnvelope);
}

const FieldDefsEnvelope = z
  .object({ field_definitions: z.array(FieldDefinition) })
  .transform((d) => d.field_definitions);

export async function fetchProjectDependencies(projectId: string): Promise<TaskDependency[]> {
  return apiRequest(`/task-module/projects/${projectId}/dependencies`, DepsEnvelope);
}

/** Общий гант: связи всех проектов, чьи задачи на канвасе — одним запросом (без N+1). */
export async function fetchDependenciesByProjects(
  projectIds: readonly string[],
): Promise<TaskDependency[]> {
  if (projectIds.length === 0) return [];
  const q = encodeURIComponent([...projectIds].join(','));
  return apiRequest(`/task-module/dependencies?project_ids=${q}`, DepsEnvelope);
}

export async function fetchProjectFieldDefinitions(projectId: string): Promise<FieldDefinition[]> {
  return apiRequest(`/task-module/projects/${projectId}/field-definitions`, FieldDefsEnvelope);
}

/** FE-41 — quick-create custom-поля из карточки (RBAC project-lead на BE-10). */
const FieldDefCreateEnvelope = z.object({ field_definition: FieldDefinition });
export type QuickCreateFieldInput = {
  key: string;
  label: string;
  type: FieldDefinition['type'];
  required: boolean;
  sequence: number;
  constraints?: Record<string, unknown>;
};
export async function createProjectFieldDefinition(
  projectId: string,
  input: QuickCreateFieldInput,
): Promise<FieldDefinition> {
  const r = await apiRequest(
    `/task-module/projects/${projectId}/field-definitions`,
    FieldDefCreateEnvelope,
    { method: 'POST', body: input },
  );
  return r.field_definition;
}

// ─── FB15 (M2M) — архивация проекта: preview + archive ──────────────

const ArchivePreviewEnvelope = z.object({
  /** shared-задачи получат новый главный проект (со сменой номера) */
  promote_count: z.number().int(),
  /** эксклюзивные задачи будут архивированы вместе с проектом */
  archive_count: z.number().int(),
  /** вторичные членства будут сняты (их per-project связи уйдут) */
  detach_count: z.number().int(),
});
export type ProjectArchivePreview = z.infer<typeof ArchivePreviewEnvelope>;

export async function fetchProjectArchivePreview(
  projectId: string,
): Promise<ProjectArchivePreview> {
  return apiRequest(`/task-module/projects/${projectId}/archive-preview`, ArchivePreviewEnvelope);
}

/**
 * F7/B11 (project-actions-parity §3.2) — архивация с обоснованием и подписью.
 *
 * Тела у ручки не было ВОВСЕ: действие, которое архивирует два десятка задач,
 * уходило по одному подтверждению, тогда как отмена — не делавшая с задачами
 * ничего — требовала подписи. Защита была перевёрнута относительно ущерба.
 */
export async function archiveProject(
  projectId: string,
  // B10 (tier2-approval-requests §5) — подписи в теле нет: Tier 2 уходит заявкой.
  body: { reason: string },
): Promise<void> {
  await apiRequest(`/task-module/projects/${projectId}`, z.unknown(), { method: 'DELETE', body });
}

// ─── Mutations: Tasks ───────────────────────────────────────────────

export type TaskCreateInput = TaskCreateRequest;

const TaskEnvelope = z
  .object({ task: Task, label_directory: LabelDirectory })
  .transform((d) => withDirectoryLabels(d.task, d.label_directory));

// BE-2 — PATCH возвращает task + assignee_directory; директорию принимаем
// строго, но patchTask отдаёт Task (директория refetch-ится через invalidate).
const TaskPatchEnvelope = z
  .object({
    task: Task,
    assignee_directory: AssigneeDirectory.optional().default([]),
    label_directory: LabelDirectory,
  })
  .transform((d) => withDirectoryLabels(d.task, d.label_directory));

export async function createTask(input: TaskCreateInput): Promise<Task> {
  const body = TaskCreateRequestSchema.parse(input);
  return apiRequest('/task-module/tasks', TaskEnvelope, { method: 'POST', body });
}

/**
 * BE-2 — PATCH принимает `assignees: TaskAssignee[]` (full-replace) среди прочих
 * полей. `patch` валидируется на BE; FE строго парсит только ответ.
 */
export async function patchTask(taskId: string, patch: Record<string, unknown>): Promise<Task> {
  return apiRequest(`/task-module/tasks/${taskId}`, TaskPatchEnvelope, {
    method: 'PATCH',
    body: patch,
  });
}

// ─── Move (перенос задачи в другой проект) ──────────────────────────

/** Перенос задачи (и поддерева) в другой проект. Ответ — тот же envelope, что PATCH. */
export async function moveTask(taskId: string, input: TaskMoveInput): Promise<Task> {
  return apiRequest(`/task-module/tasks/${taskId}/move`, TaskPatchEnvelope, {
    method: 'POST',
    body: input,
  });
}

/**
 * B1 (пак context-menu) — серверный дубль задачи. Заголовок собирает клиент
 * (суффикс «(копия)» — локализованная строка, сервер её не сочиняет).
 */
export async function duplicateTask(
  taskId: string,
  input: { include_subtree?: boolean; title?: string } = {},
): Promise<{ task: Task; subtask_count: number }> {
  return apiRequest(
    `/task-module/tasks/${taskId}/duplicate`,
    z
      .object({ task: Task, subtask_count: z.number(), label_directory: LabelDirectory })
      .transform((d) => ({
        task: withDirectoryLabels(d.task, d.label_directory),
        subtask_count: d.subtask_count,
      })),
    { method: 'POST', body: input },
  );
}

/** Preview потерь ДО подтверждения переноса (counts + ремап статуса). */
export async function fetchMovePreview(
  taskId: string,
  targetProjectId: string,
): Promise<TaskMovePreview> {
  return apiRequest(
    `/task-module/tasks/${taskId}/move-preview?target_project_id=${targetProjectId}`,
    TaskMovePreview,
  );
}

// ─── Метки задачи (эталон FD-D4 gap) ──────────────────────────────────

const LabelRef = z.object({ id: z.string(), name: z.string(), color_token: z.string() });
const LabelsEnvelope = z.object({ labels: z.array(LabelRef) }).transform((d) => d.labels);
export type TaskLabelOption = z.infer<typeof LabelRef>;

/** Full-replace состава меток задачи (+ создание новых по имени в primary-проекте). */
export async function putTaskLabels(
  taskId: string,
  input: { label_ids: string[]; create?: Array<{ name: string; color_token: string }> },
): Promise<TaskLabelOption[]> {
  return apiRequest(`/task-module/tasks/${taskId}/labels`, LabelsEnvelope, {
    method: 'PUT',
    body: input,
  });
}

/** Пул меток entity для пикера. */
export async function fetchAvailableLabels(taskId: string): Promise<TaskLabelOption[]> {
  return apiRequest(`/task-module/tasks/${taskId}/labels/available`, LabelsEnvelope);
}

/**
 * BE-7 — атомарные add/remove ОДНОГО ответственного (перенос по оси «Исполнитель»).
 * Полная замена состава (`PATCH {assignees:[…]}`) — это read-modify-write: два
 * человека, тянущие карточки одновременно, затирают назначения друг друга.
 */
const AssigneesEnvelope = z.object({
  assignees: z.array(TaskAssignee),
  assignee_directory: AssigneeDirectory.optional().default([]),
});

export async function addTaskAssignee(
  taskId: string,
  userId: string,
): Promise<z.infer<typeof AssigneesEnvelope>> {
  return apiRequest(`/task-module/tasks/${taskId}/assignees`, AssigneesEnvelope, {
    method: 'POST',
    body: { type: 'user', user_id: userId },
  });
}

/** Снять по user_id: строку-носитель находим в текущем составе (id строки знает только
 *  сервер, а вызывающему удобнее оперировать человеком). */
export async function removeTaskAssigneeByUser(
  taskId: string,
  userId: string,
): Promise<z.infer<typeof AssigneesEnvelope>> {
  const { task } = await fetchTask(taskId);
  const row = task.assignees.find((a) => a.type === 'user' && a.user_id === userId);
  if (!row?.id) return { assignees: task.assignees, assignee_directory: [] };
  return apiRequest(`/task-module/tasks/${taskId}/assignees/${row.id}`, AssigneesEnvelope, {
    method: 'DELETE',
  });
}

/**
 * BE-6 — атомарные add/remove одной метки (перенос по оси «Метка»).
 * PUT выше — полная замена состава: для перетаскивания это read-modify-write, и два
 * человека, работающих параллельно, затирали бы метки друг друга.
 */
export async function addTaskLabel(taskId: string, labelId: string): Promise<TaskLabelOption[]> {
  return apiRequest(`/task-module/tasks/${taskId}/labels/${labelId}`, LabelsEnvelope, {
    method: 'POST',
  });
}

export async function removeTaskLabel(taskId: string, labelId: string): Promise<TaskLabelOption[]> {
  return apiRequest(`/task-module/tasks/${taskId}/labels/${labelId}`, LabelsEnvelope, {
    method: 'DELETE',
  });
}

// ─── BB3/FB9 — M2M membership (add / remove / set-primary / preview) ─────

/** Ответ membership-мутаций: memberships[] + project_directory (денорм code/name). */
const MembershipBundle = z.object({
  memberships: z.array(TaskProjectMembership),
  project_directory: z.array(TaskProjectDirectoryEntry),
});
export type MembershipBundle = z.infer<typeof MembershipBundle>;

/** Добавить задачу во вторичный проект (is_primary=false). */
export async function addTaskProject(taskId: string, projectId: string): Promise<MembershipBundle> {
  return apiRequest(`/task-module/tasks/${taskId}/projects`, MembershipBundle, {
    method: 'POST',
    body: { project_id: projectId },
  });
}

/** Снять задачу с проекта (нельзя снять primary). */
export async function removeTaskProject(
  taskId: string,
  projectId: string,
): Promise<MembershipBundle> {
  return apiRequest(`/task-module/tasks/${taskId}/projects/${projectId}`, MembershipBundle, {
    method: 'DELETE',
  });
}

/** Сделать проект главным (ре-минт sequence в новом primary). */
export async function setPrimaryProject(
  taskId: string,
  projectId: string,
): Promise<MembershipBundle> {
  return apiRequest(
    `/task-module/tasks/${taskId}/projects/${projectId}/primary`,
    MembershipBundle,
    {
      method: 'POST',
      body: {},
    },
  );
}

/** Pre-flight для confirm снятия: что уйдёт при отвязке проекта. */
const MembershipPreview = z.object({
  dependency_count: z.number().int().nonnegative(),
  subtree_child_count: z.number().int().nonnegative(),
});
export type MembershipPreview = z.infer<typeof MembershipPreview>;

export async function fetchMembershipPreview(
  taskId: string,
  projectId: string,
): Promise<MembershipPreview> {
  return apiRequest(`/task-module/tasks/${taskId}/projects/${projectId}`, MembershipPreview);
}

// BE-2 — строгий вход смены ответственных (full-replace). FE валидирует
// payload перед отправкой, чтобы не дрейфовать от discriminated union shared.
const TaskAssigneesPatchInput = z.object({ assignees: z.array(TaskAssignee) }).strict();
export type TaskAssigneesPatchInput = z.infer<typeof TaskAssigneesPatchInput>;

/** BE-2 — атомарная смена состава ответственных (user/role/group). */
export async function patchTaskAssignees(
  taskId: string,
  assignees: readonly TaskAssignee[],
): Promise<Task> {
  const body = TaskAssigneesPatchInput.parse({ assignees: [...assignees] });
  return apiRequest(`/task-module/tasks/${taskId}`, TaskPatchEnvelope, { method: 'PATCH', body });
}

export async function deleteTask(taskId: string): Promise<void> {
  await apiRequest(`/task-module/tasks/${taskId}`, z.unknown(), { method: 'DELETE' });
}

/**
 * FE-37 — ответ массовой операции разбирается ЦЕЛИКОМ. Раньше клиент читал только
 * `affected`, а `denied_ids` (нет прав) и `skipped_ids` (папка, нечего сдвигать)
 * молча терялись: пользователь видел «изменено N» и не знал, что часть выборки не
 * тронута и почему.
 */
const BulkResultEnvelope = z.object({
  affected: z.number().int(),
  denied_ids: z.array(z.string()).optional().default([]),
  skipped_ids: z
    .array(z.object({ id: z.string(), reason: z.string() }))
    .optional()
    .default([]),
  /**
   * F19/B27 — строки состава, снятые человеком вручную перед применением.
   *
   * Поле обязано быть ЗДЕСЬ, а не только в схеме черновика: карточка строит
   * отчёт из ответа мутации (кэш к этому моменту может не успеть обновиться), и
   * конверт без него молча выбрасывал бы поле — «применено 2 из 3» читалось бы
   * как отказ прав, то есть как ошибка там, где было решение человека.
   */
  excluded_ids: z.array(z.string()).optional(),
});
export type BulkResult = z.infer<typeof BulkResultEnvelope>;

/**
 * chat-interactive-actions F7 (§2.4) — операции пачки приходят из `@staiter/shared`.
 *
 * Рукописный union жил здесь копией серверного: их расхождение обнаруживалось не
 * компилятором, а 422-ответом у пользователя. Теперь оба конца читают одно
 * определение, и добавленная на сервере операция появляется во фронте сама.
 */
export type BulkOp = TaskBulkOp;

export async function bulkUpdateTasks(input: {
  task_ids: readonly string[];
  op: BulkOp;
  /**
   * F7 (§2.5) — идентификатор черновика чата. Присутствует ТОЛЬКО когда пачку
   * подтвердили кнопкой на карточке: сервер по нему делает операцию одноразовой
   * (второй клик получает 409 с результатом первого). Ручной путь из панели
   * массового редактирования его не шлёт — и не должен: происхождение выводится
   * из наличия поля, а не из слов клиента (I7).
   */
  draft_id?: string;
  /** F45 (B45) — полный размер выбора, когда он режется на пачки. */
  selection_total?: number;
  /** F45 (B45) — Tier-2 подтверждение выбора больше одной пачки (иначе 422). */
  confirm_bulk?: boolean;
}): Promise<BulkResult> {
  return apiRequest('/task-module/tasks/bulk', BulkResultEnvelope, { method: 'POST', body: input });
}

/**
 * F7 / B14 — массовый комментарий. Ответ той же формы, что у `bulkUpdateTasks`
 * (I2): отчёт на карточке и в тосте строится одним кодом.
 */
export async function bulkCommentTasks(input: {
  task_ids: readonly string[];
  text: string;
  draft_id?: string;
}): Promise<BulkResult> {
  return apiRequest('/task-module/tasks/bulk-comment', BulkResultEnvelope, {
    method: 'POST',
    body: input,
  });
}

const AssigneeEnvelope = z.object({ assignee: z.unknown() }).transform((d) => d.assignee);

export async function claimTask(taskId: string, assigneeId: string): Promise<unknown> {
  return apiRequest(`/task-module/tasks/${taskId}/claim`, AssigneeEnvelope, {
    method: 'POST',
    body: { assignee_id: assigneeId },
  });
}

export async function unclaimTask(taskId: string, assigneeId: string): Promise<unknown> {
  return apiRequest(`/task-module/tasks/${taskId}/unclaim`, AssigneeEnvelope, {
    method: 'POST',
    body: { assignee_id: assigneeId },
  });
}

/**
 * F18 — `reason` в ответе НЕОБЯЗАТЕЛЬНА: с тех пор как её перестали требовать на
 * входе, сервер эхом отдаёт `undefined`, и строгая схема роняла разбор ответа —
 * мутация «падала» уже ПОСЛЕ успешной записи, тост с откатом не показывался, а
 * задача оставалась завершённой. Дефект нашёлся живым прогоном, не типами.
 */
const CloseEnvelope = z
  .object({ task: Task, reason: z.string().optional(), label_directory: LabelDirectory })
  .transform((d) => withDirectoryLabels(d.task, d.label_directory));

/**
 * F18 — причина необязательна. Пустая строка НЕ отправляется: контракт различает
 * «причины не назвали» и «назвали пустую», и подставлять выдуманную фразу за
 * пользователя нельзя — она осядет в аудите как его слова.
 */
export async function closeTask(taskId: string, reason?: string): Promise<Task> {
  return apiRequest(`/task-module/tasks/${taskId}/close`, CloseEnvelope, {
    method: 'POST',
    body: reason && reason.trim() !== '' ? { reason } : {},
  });
}

// ─── Watchers / Dependencies / Comments (S12.3) ─────────────────────

export async function watchTask(taskId: string): Promise<void> {
  await apiRequest(`/task-module/tasks/${taskId}/watchers/me`, z.unknown(), { method: 'POST' });
}

export async function unwatchTask(taskId: string): Promise<void> {
  await apiRequest(`/task-module/tasks/${taskId}/watchers/me`, z.unknown(), { method: 'DELETE' });
}

const DependencyEnvelope = z.object({ dependency: TaskDependency }).transform((d) => d.dependency);

export async function addDependency(
  taskId: string,
  // FC3 (M2M) — project_id: связь принадлежит проекту; в проектном ганте/карточке
  // передаём контекстный проект (без него BE берёт общий проект концов).
  input: { related_task_id: string; relation_type: string; project_id?: string },
): Promise<TaskDependency> {
  return apiRequest(`/task-module/tasks/${taskId}/dependencies`, DependencyEnvelope, {
    method: 'POST',
    body: input,
  });
}

export async function removeDependency(taskId: string, depId: string): Promise<void> {
  await apiRequest(`/task-module/tasks/${taskId}/dependencies/${depId}`, z.unknown(), {
    method: 'DELETE',
  });
}

// ─── Refs (AD4) — secondary привязки к ERP-сущностям ────────────────

// AD8 — BE денормализует human-код/название привязки (5 searchable-типов);
// пустые поля не шлются — FE падает на id-огрызок (fallback как у subject).
const TaskRefResolved = TaskRef.extend({
  code: z.string().optional(),
  title: z.string().optional(),
});
export type TaskRefResolved = z.infer<typeof TaskRefResolved>;
const TaskRefsEnvelope = z.object({ refs: z.array(TaskRefResolved) }).transform((d) => d.refs);
const TaskRefEnvelope = z.object({ ref: TaskRef }).transform((d) => d.ref);

export async function fetchTaskRefs(taskId: string): Promise<TaskRefResolved[]> {
  return apiRequest(`/task-module/tasks/${taskId}/refs`, TaskRefsEnvelope);
}

export async function addTaskRef(
  taskId: string,
  input: { ref_type: string; ref_id: string; role: 'mentions' | 'affects' | 'evidence' },
): Promise<TaskRef> {
  return apiRequest(`/task-module/tasks/${taskId}/refs`, TaskRefEnvelope, {
    method: 'POST',
    body: input,
  });
}

export async function removeTaskRef(taskId: string, refId: string): Promise<void> {
  await apiRequest(`/task-module/tasks/${taskId}/refs/${refId}`, z.unknown(), {
    method: 'DELETE',
  });
}

// AD6 — кандидаты привязки: поиск ERP-сущностей по типу и строке.
const RefCandidate = z.object({
  ref_type: z.string(),
  ref_id: z.string().uuid(),
  code: z.string(),
  title: z.string(),
});
export type RefCandidate = z.infer<typeof RefCandidate>;
const RefSearchEnvelope = z.object({ results: z.array(RefCandidate) }).transform((d) => d.results);

export async function searchRefCandidates(type: string, q: string): Promise<RefCandidate[]> {
  const params = new URLSearchParams({ type, q, limit: '10' });
  return apiRequest(`/task-module/tasks/refs/search?${params.toString()}`, RefSearchEnvelope);
}

const CommentEnvelope = z.object({ comment: Comment }).transform((d) => d.comment);

export async function createComment(
  taskId: string,
  input: { content_json: unknown; attachment_ids?: string[] },
): Promise<Comment> {
  return apiRequest(`/task-module/tasks/${taskId}/comments`, CommentEnvelope, {
    method: 'POST',
    body: input,
  });
}

/**
 * F21 — правка комментария, в т.ч. списка привязанных вложений.
 * `attachment_ids` не передан = список не трогаем; `[]` = отвязать все (B2).
 * Первый вызывающий PATCH-ручки, которая жила без потребителя с самого B2.
 */
export async function patchComment(
  taskId: string,
  commentId: string,
  input: { content_json: unknown; attachment_ids?: string[] },
): Promise<Comment> {
  return apiRequest(`/task-module/tasks/${taskId}/comments/${commentId}`, CommentEnvelope, {
    method: 'PATCH',
    body: input,
  });
}

export async function deleteComment(taskId: string, commentId: string): Promise<void> {
  await apiRequest(`/task-module/tasks/${taskId}/comments/${commentId}`, z.unknown(), {
    method: 'DELETE',
  });
}

// BE-3 — нормализованный ответ истории: per-field diff (`changes`) + total-count.
export type TaskAuditEvent = AuditEventView;
export type TaskAuditPage = { events: AuditEventView[]; total: number; nextCursor: string | null };

export async function fetchTaskAudit(taskId: string): Promise<TaskAuditPage> {
  const r = await apiRequest(`/task-module/tasks/${taskId}/audit`, AuditEventListResponse);
  return { events: r.audit_events, total: r.total, nextCursor: r.next_cursor };
}

// ─── Mutations: Projects / States ───────────────────────────────────

const ProjectEnvelope = z.object({ project: Project }).transform((d) => d.project);

export async function createProject(input: {
  name: string;
  description?: string | null;
  owner_user_id: string;
  kind: string;
  members?: string[];
}): Promise<Project> {
  return apiRequest('/task-module/projects', ProjectEnvelope, { method: 'POST', body: input });
}

/**
 * Get-or-create дефолтный проект «Входящие» для активной компании (со статусами).
 * Снимает барьер «создай проект, чтобы создать задачу» на пустой компании.
 */
export async function ensureInboxProject(): Promise<Project> {
  return apiRequest('/task-module/projects/inbox', ProjectEnvelope, { method: 'POST', body: {} });
}

const StateEnvelope = z.object({ state: State }).transform((d) => d.state);

export async function createState(
  projectId: string,
  input: { name: string; color_token: string; group: string; sequence: number },
): Promise<State> {
  return apiRequest(`/task-module/projects/${projectId}/states`, StateEnvelope, {
    method: 'POST',
    body: input,
  });
}

export async function patchState(
  projectId: string,
  stateId: string,
  patch: Record<string, unknown>,
): Promise<State> {
  return apiRequest(`/task-module/projects/${projectId}/states/${stateId}`, StateEnvelope, {
    method: 'PATCH',
    body: patch,
  });
}

export async function deleteState(
  projectId: string,
  stateId: string,
  targetStateId: string,
): Promise<void> {
  await apiRequest(`/task-module/projects/${projectId}/states/${stateId}`, z.unknown(), {
    method: 'DELETE',
    body: { target_state_id: targetStateId },
  });
}
